// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";

import { mdiInformation } from "@mdi/js";
import { Badge, Divider, Popover, Space } from "antd";
import { display } from "store/features/userDocumentation/documentationSlice";
import styled from "styled-components/macro";

import { useSupportTicketStore } from "components/support";
import { selectRequestTour, useTourStore } from "components/tour/tourStore";
import { useWalkthroughEnabled } from "components/tour/useWalkthroughEnabled";
import UserMenuItem from "components/user/UserMenuItem";

import { usePost } from "./context";
import { useNewPostsCountManager } from "./hooks";
import { DocumentationTab } from "./types";

const DocumentationToggle = ({ className = "" }) => {
  // hooks
  const [{ totalNewPosts }] = usePost();
  useNewPostsCountManager();

  // local state
  const [menuVisible, setMenuVisible] = useState(false);

  // dispatches
  const dispatch = useDispatch();
  const openSupportTicket = useSupportTicketStore((s) => s.open);
  const requestTour = useTourStore(selectRequestTour);
  const walkthroughEnabled = useWalkthroughEnabled();

  const close = () => setMenuVisible(false);

  const openDocs = useCallback(() => {
    // Set default tab to "Notifications" if there are new posts, otherwise let modal use its default
    const defaultTab = totalNewPosts > 0 ? DocumentationTab.Notifications : undefined;
    dispatch(display({ defaultTab }));
    close();
  }, [dispatch, totalNewPosts]);

  const openSupport = () => {
    openSupportTicket();
    close();
  };

  const startTour = () => {
    requestTour();
    close();
  };

  const menu = (
    <MenuWrapper direction="vertical" split={<StyledDivider />}>
      {walkthroughEnabled && (
        <UserMenuItem testId="help-menu-walkthrough" onClick={startTour}>
          Guided Walkthrough
        </UserMenuItem>
      )}
      <UserMenuItem testId="help-menu-documentation" onClick={openDocs}>
        Tutorials &amp; Notifications
      </UserMenuItem>
      <UserMenuItem testId="help-menu-support-ticket" onClick={openSupport}>
        Open Support Ticket
      </UserMenuItem>
    </MenuWrapper>
  );

  return (
    <Popover
      arrowPointAtCenter
      content={menu}
      // Named so the guided walkthrough can wait for this menu specifically
      // rather than any open popover.
      overlayClassName="help-menu-popover"
      placement="bottomRight"
      trigger="click"
      open={menuVisible}
      onOpenChange={setMenuVisible}>
      <NotificationButton
        className={className}
        aria-label="Help"
        data-testid="documentation-toggle-help-icon">
        <Badge
          size="small"
          count={totalNewPosts}
          overflowCount={10}
          color="var(--color-primary)">
          <Icon path={mdiInformation} size="26px" />
        </Badge>
      </NotificationButton>
    </Popover>
  );
};

export default DocumentationToggle;

const MenuWrapper = styled(Space)`
  width: 20rem;
  color: var(--color-text-60);
  margin: -4px -16px;
`;

const StyledDivider = styled(Divider)`
  margin: 0;
`;

const NotificationButton = styled.button`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: none;
  font-size: 24px;
  transition: color 0.2s, background-color 0.2s;
  cursor: pointer;

  svg {
    color: var(--color-text-40);
  }

  &:hover svg {
    color: var(--color-text);
  }
`;
